/**
 * StaleWebApkDialog — Modal accionable para el caso WebAPK stale (P-196).
 *
 * SPEC-130 (P-196 — QA SITE Sprint 58, Founder Anderson 2026-09-06):
 *   Tras desinstalar la PWA, Chrome Android puede reportar el WebAPK como
 *   instalado (vía `getInstalledRelatedApps()`) aunque el usuario ya lo
 *   haya desinstalado. En ese estado `beforeinstallprompt` NO dispara →
 *   el hook `useInstallPrompt` marca `webapkStale=true` (tras 2s de
 *   gracia). El click en `InstallAppButton` abre ESTE modal en lugar del
 *   toast efímero — los pasos requieren acción manual persistente.
 *
 * El toast warning (`showAlreadyInstalledGuidance(true)`) también se
 * muestra (efímero 15s), pero el modal persiste hasta que el usuario lo
 * cierra — necesario porque los pasos son secuenciales y el usuario
 * puede necesitar seguirlos fuera del browser (Ajustes del sistema,
 * cerrar Chrome, volver a la página).
 *
 * Los 3 pasos son los pasos estándar documentados por Chrome para
 * limpiar WebAPK stale (verificación §6 Anti-Invención — no inventados):
 *   1. Ajustes → Aplicaciones → Chrome → Almacenamiento → "Borrar caché"
 *      (NO "Borrar datos" — eso cerraría la sesión del usuario).
 *   2. Cerrar Chrome completamente (deslizar fuera del app switcher) y
 *      volver a abrirlo.
 *   3. Volver a esta página y hacer click en "Instalar App". Si el
 *      problema persiste, esperar 24h — Chrome sincroniza su estado de
 *      WebAPK en segundo plano.
 *
 * Trazabilidad §6: SPEC-130 §1.4 Task B.1. Puro componente de UI —
 * cero lógica de negocio. Reusa `Dialog`/`Button` existentes — 0 nuevos
 * deps npm.
 */

'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Settings, RefreshCw, Clock } from 'lucide-react';

export interface StaleWebApkDialogProps {
  /** Controla la visibilidad del modal (controlled pattern). */
  open: boolean;
  /** Callback invocado al cerrar el modal (botón "Entendido" o backdrop/X). */
  onClose: () => void;
}

/**
 * Modal con 3 pasos accionables para limpiar WebAPK stale en Android
 * Chrome. Renderiza `null` implícito vía Radix Dialog cuando `open=false`
 * (el componente Dialog maneja su propia visibilidad internamente).
 */
export function StaleWebApkDialog({ open, onClose }: StaleWebApkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Chrome cree que Profileia sigue instalada
          </DialogTitle>
          <DialogDescription>
            Acabas de desinstalar la app pero Chrome mantiene un registro obsoleto (WebAPK stale).
            Sigue estos pasos para reinstalar:
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-slate-700 dark:text-slate-300 my-4">
          <li className="flex gap-3">
            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold">1</span>
            <span className="flex items-start gap-2">
              <Settings className="h-4 w-4 mt-0.5 text-slate-500" />
              <span>Abre <strong>Ajustes → Aplicaciones → Chrome → Almacenamiento</strong> y toca <strong>&quot;Borrar caché&quot;</strong> (NO &quot;Borrar datos&quot; — eso cerraría tu sesión).</span>
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold">2</span>
            <span className="flex items-start gap-2">
              <RefreshCw className="h-4 w-4 mt-0.5 text-slate-500" />
              <span>Cierra Chrome completamente (desliza hacia arriba desde el bottom y desliza Chrome fuera) y vuelve a abrirlo.</span>
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold">3</span>
            <span className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-slate-500" />
              <span>Vuelve a esta página y haz click en <strong>&quot;Instalar App&quot;</strong>. Si el problema persiste, espera <strong>24 horas</strong> — Chrome sincroniza su estado de WebAPK en segundo plano.</span>
            </span>
          </li>
        </ol>
        <DialogFooter>
          <Button onClick={onClose} className="w-full">Entendido</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
